import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

/**
 * Read from import.meta.env, which vite.config.ts widens to include the FIREBASE_ prefix
 * already used by this repo's .env.
 *
 * Missing values fail loudly at startup rather than at the first Firestore call. An
 * undefined projectId produces "Missing or insufficient permissions" from a completely
 * unrelated query later, which is a miserable thing to debug.
 */
function required(name: string): string {
  const value = import.meta.env[name as keyof ImportMetaEnv] as string | undefined;
  if (!value) {
    throw new Error(
      `Missing ${name}. Copy .env.example to .env and fill in the Firebase web config.`
    );
  }
  return value;
}

export const app = initializeApp({
  apiKey: required('FIREBASE_API_KEY'),
  authDomain: required('FIREBASE_AUTH_DOMAIN'),
  projectId: required('FIREBASE_PROJECT_ID'),
  storageBucket: required('FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('FIREBASE_APP_ID'),
});

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

/**
 * Cloud Functions, pinned to the region the functions are actually deployed in.
 *
 * They are v1 functions declared without an explicit region, which means us-central1.
 * `getFunctions(app)` defaults to us-central1 too, so naming it changes nothing today —
 * but a default that happens to match is not the same as a stated one, and a callable
 * resolved against the wrong region fails as an opaque `functions/internal`.
 *
 * The functions themselves live in the zsoogi-ios repo, not here.
 */
export const functions = getFunctions(app, 'us-central1');
